import { jsPDF } from "jspdf";

export const GIFT_CARD_WIDTH_MM = 85.6;
export const GIFT_CARD_HEIGHT_MM = 54;
const VOUCHER_CODE_MAX_LINES = 2;
const VOUCHER_CODE_MIN_FONT_SIZE = 6;

export type GiftCardVoucher = { code: string; value: number };
export type GiftCardBrand = { name?: string | null; logoUrl?: string | null } | null;
export type GiftCardPdfDependencies = { loadLogo?: () => Promise<string> };

export async function createGiftCardPdf(
  voucher: GiftCardVoucher,
  store: GiftCardBrand,
  dependencies: GiftCardPdfDependencies = {},
) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [GIFT_CARD_WIDTH_MM, GIFT_CARD_HEIGHT_MM],
  });
  const cardWidth = GIFT_CARD_WIDTH_MM;
  const cardHeight = GIFT_CARD_HEIGHT_MM;

  doc.setFillColor(11, 54, 46);
  doc.roundedRect(1, 1, cardWidth - 2, cardHeight - 2, 3, 3, "F");
  doc.setDrawColor(211, 179, 112);
  doc.setLineWidth(0.5);
  doc.roundedRect(2.5, 2.5, cardWidth - 5, cardHeight - 5, 2.5, 2.5, "S");

  let logoIncluded = false;
  if (store?.logoUrl && dependencies.loadLogo) {
    try {
      const logoData = await dependencies.loadLogo();
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(6, 6, 14, 14, 2, 2, "F");
      doc.addImage(logoData, 7, 7, 12, 12, undefined, "FAST");
      logoIncluded = true;
    } catch {
      // A missing optional logo must not prevent the card from downloading.
    }
  }

  const storeName = store?.name?.trim() || "Your Store";
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  const brandLines = doc.splitTextToSize(storeName.toUpperCase(), 58).slice(0, 2);
  doc.text(brandLines, logoIncluded ? 23 : 7, 10);

  doc.setTextColor(224, 199, 144);
  doc.setFontSize(7);
  doc.text("GIFT VOUCHER", 7, 25);

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  let valueFontSize = 20;
  // jsPDF's built-in Helvetica font lacks the Naira glyph; use the ISO
  // currency code so the PDF never renders the symbol as a missing-glyph bar.
  const valueText = `NGN ${Math.max(0, Number(voucher.value)).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  while (valueFontSize > 13) {
    doc.setFontSize(valueFontSize);
    if (doc.getTextWidth(valueText) <= 43) break;
    valueFontSize -= 1;
  }
  doc.text(valueText, cardWidth - 7, 35, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(224, 235, 230);
  doc.text("VOUCHER NUMBER", 7, 38);
  doc.setFont("helvetica", "bold");
  let codeFontSize = 9;
  doc.setFontSize(codeFontSize);
  let codeLines = doc.splitTextToSize(voucher.code, cardWidth - 14);
  while (codeLines.length > VOUCHER_CODE_MAX_LINES && codeFontSize > VOUCHER_CODE_MIN_FONT_SIZE) {
    codeFontSize = Math.max(VOUCHER_CODE_MIN_FONT_SIZE, codeFontSize - 0.5);
    doc.setFontSize(codeFontSize);
    codeLines = doc.splitTextToSize(voucher.code, cardWidth - 14);
  }
  if (codeLines.length > VOUCHER_CODE_MAX_LINES) {
    throw new Error("Voucher number is too long to print legibly on a gift card.");
  }
  doc.setTextColor(255, 255, 255);
  doc.text(codeLines, 7, 42.5, { lineHeightFactor: 1.2 });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(224, 235, 230);
  doc.text("Voucher balance is verified at checkout", 7, cardHeight - 3.5);

  return { doc, logoIncluded };
}
